import { Typography } from '@/components/ui/typography'
import { Label } from '@/components/ui/label'
import { useEffect, useMemo, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { z } from 'zod'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescriptionHidden,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MetricCombobox } from '@/components/custom/metrics/metric-combobox'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { getScorecardDefaultDates } from '@/features/scorecard/scorecard-default-dates'
import {
  getScorecardStatusOptionLabel,
  isScorecardKpiStatus,
  scorecardStatusOptionsFor,
} from '@/features/scorecard/scorecard-status'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { formatScorecardValue } from '@/features/scorecard/scorecard-table.utils'
import {
  useCreateScorecardKpi,
  useScorecardMetricOptionsForPeriods,
  useUpdateScorecardKpi,
} from '@/hooks/use-scorecard'
import { formatDate } from '@/lib/format'
import { showSuccess } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type {
  ScorecardActualDto,
  ScorecardKpiDirection,
  ScorecardKpiDto,
  ScorecardKpiStatus,
  ScorecardMetricDataType,
  ScorecardMetricOptionDto,
  ScorecardMetricTypeDto,
  ScorecardPeriodType,
  ScorecardStatusSource,
  ScorecardTargetMode,
  ScorecardTrackedKpiDto,
} from '@/services/scorecard.service'

const COMPARISON_PERIOD_OPTIONS: {
  value: ScorecardPeriodType
  label: string
}[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'LTM', label: 'LTM' },
  { value: 'AS_OF', label: 'As of' },
]
const COMPARISON_PERIOD_VALUES = COMPARISON_PERIOD_OPTIONS.map(
  (option) => option.value
)

const FIELD_BOX = 'rounded-lg border border-primary/10 bg-primary/[0.015] p-3'
const LABEL_ROW = 'flex h-5 items-center'

const DIRECTION_OPTIONS: { value: ScorecardKpiDirection; label: string }[] = [
  { value: 'HIGHER_IS_BETTER', label: 'Increase' },
  { value: 'LOWER_IS_BETTER', label: 'Decrease' },
]

const scorecardKpiSchema = z
  .object({
    metricLabelId: z.string().min(1, 'Objective is required'),
    periodType: z.enum([
      'MONTHLY',
      'QUARTERLY',
      'LTM',
      'TTM',
      'YTD',
      'ANNUAL',
      'AS_OF',
    ]),
    targetValue: z.number().or(z.nan()).optional(),
    targetMode: z.enum(['ABSOLUTE', 'PERCENT_CHANGE']),
    targetPercent: z.number().or(z.nan()).optional(),
    startDate: z.string().min(1, 'Goal start date is required'),
    targetDate: z.string().min(1, 'Goal date is required'),
    status: z
      .enum([
        'NOT_STARTED',
        'ON_TRACK',
        'AT_RISK',
        'OFF_TRACK',
        'ACHIEVED',
        'ON_HOLD',
      ])
      .optional(),
    statusSource: z.enum(['MANUAL', 'CALCULATED']),
    direction: z.enum(['HIGHER_IS_BETTER', 'LOWER_IS_BETTER']),
  })
  .superRefine((values, context) => {
    if (values.startDate > values.targetDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Starting date must be on or before goal date',
      })
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetDate'],
        message: 'Starting date must be on or before goal date',
      })
    }

    if (values.targetMode === 'PERCENT_CHANGE') {
      if (
        !Number.isFinite(values.targetPercent) ||
        (values.targetPercent ?? 0) <= 0
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetPercent'],
          message: 'Enter a percentage above zero',
        })
      }
    } else if (!Number.isFinite(values.targetValue)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetValue'],
        message: 'Goal is required',
      })
    }
  })

type ScorecardKpiFormValues = z.infer<typeof scorecardKpiSchema>

type MetricOptionWithPeriods = ScorecardMetricOptionDto & {
  availablePeriods: ScorecardPeriodType[]
  actualsByPeriod: Partial<
    Record<ScorecardPeriodType, ScorecardActualDto | null>
  >
}

type ScorecardKpiMutationPayload = {
  periodType: ScorecardKpiFormValues['periodType']
  targetValue?: number
  targetMode: ScorecardTargetMode
  targetPercent?: number
  startValue: number
  startDate: string
  targetDate: string
  status?: ScorecardKpiStatus
  statusSource?: ScorecardStatusSource
  direction: ScorecardKpiDirection
  displayName?: string
  unitLabel?: string
  notes?: string
}

function toDateInputValue(
  value: string | undefined | null,
  fallbackValue = `${new Date().getFullYear()}-12-31`
) {
  if (!value) return fallbackValue
  return value.slice(0, 10)
}

function dataTypeOf(
  source:
    | {
        dataType?: ScorecardMetricDataType | null
        metricType?: ScorecardMetricTypeDto | null
      }
    | undefined
    | null
): ScorecardMetricDataType | undefined {
  return source?.dataType ?? source?.metricType?.dataType ?? undefined
}

function newGoalDefaults(
  defaultDates: ReturnType<typeof getScorecardDefaultDates>,
  trackedKpi?: ScorecardTrackedKpiDto | null
): ScorecardKpiFormValues {
  return {
    metricLabelId: trackedKpi?.metricLabelId ?? '',
    periodType: 'MONTHLY',
    targetValue: Number.NaN,
    targetMode: 'ABSOLUTE',
    targetPercent: undefined,
    startDate: defaultDates.startDate,
    targetDate: defaultDates.targetDate,
    status: 'NOT_STARTED',
    statusSource: 'CALCULATED',
    direction: 'HIGHER_IS_BETTER',
  }
}

function sortComparisonPeriods(periods: ScorecardPeriodType[]) {
  return COMPARISON_PERIOD_VALUES.filter((period) => periods.includes(period))
}

function formatActualBasisLabel(periodType: ScorecardPeriodType) {
  if (periodType === 'MONTHLY') return 'monthly'
  if (periodType === 'QUARTERLY') return 'quarterly'
  if (periodType === 'LTM') return 'LTM'
  if (periodType === 'AS_OF') return 'as-of'
  if (periodType === 'ANNUAL') return 'annual'
  if (periodType === 'YTD') return 'YTD'
  if (periodType === 'TTM') return 'TTM'
  return String(periodType).toLowerCase()
}

function TargetBasisLabel() {
  return (
    <div className="flex items-center gap-1.5">
      <FormLabel>Measure actuals as *</FormLabel>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex size-4 items-center justify-center rounded-full text-primary/70 transition-colors hover:text-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="What does measure actuals as mean?"
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-72 leading-5">
          <p>
            Choose the number to compare with your goal. Monthly means one
            month's result; quarterly means one quarter's result. LTM means the
            last 12 months. As of means a count or balance at a date, such as
            users or franchises. Set the goal deadline in To date.
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

function CurrentValueRow({
  actual,
  dataType,
  unitLabel,
  periodType,
}: {
  actual: ScorecardActualDto | null | undefined
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
  periodType: ScorecardPeriodType
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-primary/10 pt-2.5">
      {actual ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Typography
            variant="body-sm"
            as="span"
            className="font-medium text-primary/70"
          >
            Current value
          </Typography>
          <Typography
            variant="body-sm"
            as="span"
            className="font-medium tabular-nums text-primary"
          >
            {formatScorecardValue(actual.value, dataType, unitLabel)}
          </Typography>
          <Typography variant="body-sm" as="span" className="text-primary/70">
            as of {formatDate(actual.asOfDate ?? actual.periodEndDate)}
          </Typography>
        </div>
      ) : (
        <Typography variant="body-sm" as="span" className="text-primary/70">
          No {formatActualBasisLabel(periodType)} actual available for this
          objective yet.
        </Typography>
      )}
      <Typography
        variant="body-sm"
        as="span"
        className="italic text-primary/70"
      >
        Source: Chronograph
      </Typography>
    </div>
  )
}

interface ScorecardKpiModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: string
  fiscalYearEnd?: string | null
  kpi?: ScorecardKpiDto | null
  trackedKpi?: ScorecardTrackedKpiDto | null
}

export function ScorecardKpiModal({
  open,
  onOpenChange,
  companyId,
  fiscalYearEnd,
  kpi,
  trackedKpi,
}: ScorecardKpiModalProps) {
  const isEditMode = Boolean(kpi)
  const dialogTitleRef = useRef<HTMLHeadingElement>(null)
  const defaultDates = useMemo(
    () => getScorecardDefaultDates(fiscalYearEnd),
    [fiscalYearEnd]
  )

  const form = useForm<ScorecardKpiFormValues>({
    resolver: standardSchemaResolver(scorecardKpiSchema),
    defaultValues: newGoalDefaults(defaultDates),
  })

  const selectedComparisonPeriod = useWatch({
    control: form.control,
    name: 'periodType',
  })
  const selectedMetricLabelId = useWatch({
    control: form.control,
    name: 'metricLabelId',
  })
  const selectedStatus = useWatch({
    control: form.control,
    name: 'status',
  })
  const selectedStatusSource = useWatch({
    control: form.control,
    name: 'statusSource',
  })
  const selectedDirection = useWatch({
    control: form.control,
    name: 'direction',
  })
  const selectedTargetMode = useWatch({
    control: form.control,
    name: 'targetMode',
  })
  const unitLabel = kpi?.unitLabel ?? ''
  const metricOptions = useScorecardMetricOptionsForPeriods(
    companyId,
    COMPARISON_PERIOD_VALUES,
    open,
    isEditMode ? kpi?.metricLabelId : trackedKpi?.metricLabelId
  )
  const createKpi = useCreateScorecardKpi(companyId)
  const updateKpi = useUpdateScorecardKpi(companyId)

  useEffect(() => {
    if (!open) {
      form.reset()
      return
    }

    form.reset(
      kpi
        ? {
            metricLabelId: kpi.metricLabelId,
            periodType: kpi.periodType,
            targetValue: kpi.targetValue,
            targetMode: kpi.targetMode ?? 'ABSOLUTE',
            targetPercent: kpi.targetPercent ?? undefined,
            startDate: toDateInputValue(kpi.startDate, defaultDates.startDate),
            targetDate: toDateInputValue(
              kpi.targetDate,
              defaultDates.targetDate
            ),
            status: kpi.status,
            statusSource: kpi.statusSource ?? 'MANUAL',
            direction: kpi.direction,
          }
        : newGoalDefaults(defaultDates, trackedKpi)
    )
  }, [defaultDates, form, kpi, open, trackedKpi])

  const isSubmitting = createKpi.isPending || updateKpi.isPending
  const options = useMemo<MetricOptionWithPeriods[]>(() => {
    const byMetricId = new Map<string, MetricOptionWithPeriods>()

    for (const response of metricOptions.data) {
      for (const option of response.options) {
        const existing = byMetricId.get(option.metricLabelId)
        if (existing) {
          existing.availablePeriods = sortComparisonPeriods([
            ...existing.availablePeriods,
            response.periodType,
          ])
          existing.actualsByPeriod[response.periodType] = option.actual ?? null
          continue
        }

        byMetricId.set(option.metricLabelId, {
          ...option,
          availablePeriods: [response.periodType],
          actualsByPeriod: {
            [response.periodType]: option.actual ?? null,
          },
        })
      }
    }

    return Array.from(byMetricId.values())
  }, [metricOptions.data])
  useEffect(() => {
    // Loading options must never rewrite a saved objective's measurement period.
    // Creation can choose a default only after all period requests have settled.
    if (
      !open ||
      !selectedMetricLabelId ||
      isEditMode ||
      metricOptions.isFetching ||
      metricOptions.isLoading ||
      metricOptions.isError
    )
      return

    const availablePeriods = options.find(
      (option) => option.metricLabelId === selectedMetricLabelId
    )?.availablePeriods

    if (!availablePeriods?.length) return

    const currentPeriod = form.getValues('periodType')
    if (availablePeriods.includes(currentPeriod)) return

    form.setValue('periodType', availablePeriods[0], {
      shouldDirty: true,
      shouldValidate: true,
    })
  }, [
    form,
    open,
    options,
    selectedMetricLabelId,
    isEditMode,
    metricOptions.isFetching,
    metricOptions.isLoading,
    metricOptions.isError,
  ])
  const metricComboboxOptions = useMemo(
    () =>
      options.map((option) => ({
        id: option.metricLabelId,
        label: option.labeledAs,
        labeledAs: option.labeledAs,
        sectionName: option.globalSectionName ?? 'Metrics',
        category: option.metricType?.category,
        description:
          option.metricType?.name ?? option.globalSectionName ?? undefined,
        sortOrder: option.sortOrder ?? null,
      })),
    [options]
  )
  const selectedOption = options.find(
    (option) => option.metricLabelId === selectedMetricLabelId
  )
  const visibleComparisonPeriodOptions = COMPARISON_PERIOD_OPTIONS.filter(
    (option) =>
      selectedOption?.availablePeriods.includes(option.value) ||
      (isEditMode && option.value === kpi?.periodType)
  )
  const selectedDataType = dataTypeOf(
    isEditMode ? kpi?.metricLabel : selectedOption
  )
  const selectedActual =
    selectedOption?.actualsByPeriod[selectedComparisonPeriod] ??
    (isEditMode && kpi?.periodType === selectedComparisonPeriod
      ? kpi.actual
      : null) ??
    (trackedKpi?.actual?.periodType === selectedComparisonPeriod
      ? trackedKpi.actual
      : null)
  // Stopgap: the baseline should be the actual dated on or before startDate,
  // which only the backend can resolve. See docs/canonical/tech-debt.md.
  const startValue = selectedActual?.value ?? kpi?.startValue ?? 0

  const onSubmit = async (values: ScorecardKpiFormValues) => {
    const payload: ScorecardKpiMutationPayload = {
      periodType: values.periodType,
      ...(values.targetMode === 'PERCENT_CHANGE'
        ? {
            targetMode: 'PERCENT_CHANGE' as const,
            targetPercent: values.targetPercent,
          }
        : { targetMode: 'ABSOLUTE' as const, targetValue: values.targetValue }),
      startValue,
      startDate: values.startDate,
      targetDate: values.targetDate,
      ...(values.statusSource === 'CALCULATED'
        ? { statusSource: 'CALCULATED' as const }
        : { status: values.status }),
      direction: values.direction,
      displayName: kpi?.displayName ?? trackedKpi?.displayName ?? '',
      unitLabel,
      notes: kpi?.notes ?? '',
    }

    try {
      if (kpi) {
        await updateKpi.mutateAsync({ kpiId: kpi.id, data: payload })
        showSuccess('Objective updated')
      } else {
        await createKpi.mutateAsync({
          ...payload,
          metricLabelId: values.metricLabelId,
        })
        showSuccess('Objective added to scorecard')
      }

      onOpenChange(false)
    } catch {
      // Axios interceptor shows the error toast. Keep the modal open.
    }
  }

  const percentTargetHint =
    selectedTargetMode === 'PERCENT_CHANGE'
      ? 'A proportion of the starting value, not points added to it: 40% raised by 10% is 44%.'
      : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100vh-2rem)] max-w-2xl gap-0 overflow-y-auto p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          dialogTitleRef.current?.focus()
        }}
      >
        <DialogHeader className="border-b border-primary/10 px-5 py-4">
          <DialogTitle
            ref={dialogTitleRef}
            tabIndex={-1}
            className="text-[20px] focus:outline-none"
          >
            {isEditMode
              ? 'Edit scorecard objective'
              : 'Add objective to scorecard'}
          </DialogTitle>
          <DialogDescriptionHidden>
            Choose what to increase or decrease and the goal to reach by a date.
            Actuals come from Chronograph.
          </DialogDescriptionHidden>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4 px-5 py-4">
              <div className={FIELD_BOX}>
                <Typography
                  variant="overline"
                  className="mb-3 block text-primary/70"
                >
                  Objective
                </Typography>

                <div className="grid gap-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
                  <FormField
                    control={form.control}
                    name="direction"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={LABEL_ROW}>Direction *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select direction" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {DIRECTION_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {isEditMode ? (
                    <FormItem>
                      <div className={cn(LABEL_ROW, 'gap-2')}>
                        <Label>Metric *</Label>
                        <Badge variant="compact">
                          Imported from Chronograph
                        </Badge>
                      </div>
                      <div className="flex min-h-9 items-center rounded-md border border-primary/10 bg-primary/[0.025] px-3 text-xs text-primary">
                        <Typography
                          variant="body-sm"
                          as="span"
                          className="truncate font-medium"
                        >
                          {kpi?.metricLabel.labeledAs ?? ''}
                        </Typography>
                      </div>
                    </FormItem>
                  ) : (
                    <FormField
                      control={form.control}
                      name="metricLabelId"
                      render={({ field }) => (
                        <FormItem>
                          <div className={cn(LABEL_ROW, 'gap-2')}>
                            <FormLabel>Metric *</FormLabel>
                            <Badge variant="compact">
                              Imported from Chronograph
                            </Badge>
                          </div>
                          <MetricCombobox
                            options={metricComboboxOptions}
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={metricOptions.isLoading}
                            isLoading={metricOptions.isLoading}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>

              <div className={FIELD_BOX}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name={
                      selectedTargetMode === 'PERCENT_CHANGE'
                        ? 'targetPercent'
                        : 'targetValue'
                    }
                    render={({ field }) => (
                      <FormItem>
                        <div className={cn(LABEL_ROW, 'justify-between gap-2')}>
                          <FormLabel>Goal *</FormLabel>
                          <div className="flex items-center gap-1">
                            {(['ABSOLUTE', 'PERCENT_CHANGE'] as const).map(
                              (mode) => (
                                <Button
                                  key={mode}
                                  type="button"
                                  size="sm"
                                  variant={
                                    selectedTargetMode === mode
                                      ? 'default'
                                      : 'outline'
                                  }
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    form.setValue('targetMode', mode)
                                    form.clearErrors([
                                      'targetValue',
                                      'targetPercent',
                                    ])
                                  }}
                                >
                                  {mode === 'ABSOLUTE' ? 'Value' : '%'}
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                        <FormControl>
                          <ScorecardTargetValueInput
                            name={field.name}
                            ref={field.ref}
                            value={field.value ?? Number.NaN}
                            dataType={selectedDataType}
                            unitLabel={unitLabel}
                            mode={selectedTargetMode}
                            direction={selectedDirection}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        {percentTargetHint ? (
                          <Typography
                            variant="body-sm"
                            className="text-primary/70"
                          >
                            {percentTargetHint}
                          </Typography>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From date *</FormLabel>
                        <FormControl>
                          <ScorecardDatePicker
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="targetDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>To date *</FormLabel>
                        <FormControl>
                          <ScorecardDatePicker
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {selectedMetricLabelId ? (
                  <CurrentValueRow
                    actual={selectedActual}
                    dataType={selectedDataType}
                    unitLabel={unitLabel}
                    periodType={selectedComparisonPeriod}
                  />
                ) : null}
              </div>

              {visibleComparisonPeriodOptions.length > 1 ? (
                <FormField
                  control={form.control}
                  name="periodType"
                  render={({ field }) => (
                    <FormItem>
                      <TargetBasisLabel />
                      <FormControl>
                        <div
                          role="group"
                          aria-label="Measure actuals as"
                          className="inline-grid w-full grid-flow-col auto-cols-fr rounded-md border border-primary/10 bg-white p-1 sm:w-auto"
                        >
                          {visibleComparisonPeriodOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={cn(
                                'cursor-pointer rounded px-3 py-1.5 text-xs text-primary/70 transition-colors hover:bg-primary/5',
                                selectedComparisonPeriod === option.value &&
                                  'bg-primary text-primary-foreground hover:bg-primary'
                              )}
                              aria-pressed={
                                selectedComparisonPeriod === option.value
                              }
                              onClick={() => field.onChange(option.value)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </FormControl>
                      <Typography variant="body-sm" className="text-primary/70">
                        Use As of for a count at a date, such as users or
                        franchises. The goal's duration comes from the dates
                        above.
                      </Typography>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="statusSource"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select
                      value={
                        field.value === 'CALCULATED'
                          ? 'CALCULATED'
                          : (selectedStatus ?? 'NOT_STARTED')
                      }
                      onValueChange={(value) => {
                        if (value === 'CALCULATED') {
                          form.setValue('statusSource', 'CALCULATED')
                          return
                        }

                        if (!isScorecardKpiStatus(value)) return
                        form.setValue('statusSource', 'MANUAL')
                        form.setValue('status', value)
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CALCULATED">
                          Automatic
                          {kpi?.calculatedStatus
                            ? ` — ${getScorecardStatusOptionLabel(kpi.calculatedStatus)}`
                            : ''}
                        </SelectItem>
                        {scorecardStatusOptionsFor(kpi?.status).map(
                          (status) => (
                            <SelectItem key={status} value={status}>
                              {getScorecardStatusOptionLabel(status)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    {kpi?.calculatedStatus &&
                    selectedStatusSource === 'MANUAL' &&
                    kpi.calculatedStatus !== selectedStatus ? (
                      <div className="flex items-center gap-2">
                        <Typography
                          variant="body-sm"
                          className="text-primary/70"
                        >
                          Automatic status says{' '}
                          {getScorecardStatusOptionLabel(kpi.calculatedStatus)}
                        </Typography>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            form.setValue('statusSource', 'CALCULATED')
                          }
                        >
                          Use automatic
                        </Button>
                      </div>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="border-t border-primary/10 px-5 py-4">
              <Button
                type="button"
                variant="outline-muted"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                    ? 'Save objective'
                    : 'Add objective'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
